import { useCallback, useEffect, useRef, type ReactElement } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import type { PlatformBridge } from '@slate/core';
import { ImageViewer } from './ImageViewer';

export interface ImageLightboxProps {
  /** Platform bridge — re-required by the underlying ImageViewer's prop shape. */
  platform: PlatformBridge;
  /** Already-resolved URL of the image (asset protocol on Tauri, blob on web). */
  src: string;
  /** Display name / alt text; falls back to the URL's basename. */
  name?: string;
  /** Close handler — modal dismisses on Escape, backdrop click, or close button. */
  onClose: () => void;
}

/**
 * Full-screen modal wrapper around {@link ImageViewer} for previewing a
 * markdown-embedded image. Reuses the existing fit/zoom/pan UI by handing
 * the image's already-resolved URL through {@link ImageViewer.directUrl}.
 * Portaled to `document.body` so it floats above the editor/sidebar.
 */
export function ImageLightbox({
  platform,
  src,
  name,
  onClose,
}: ImageLightboxProps): ReactElement | null {
  const restoreFocus = useRef<Element | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    restoreFocus.current = document.activeElement;
    panelRef.current?.focus();
    return () => {
      const el = restoreFocus.current;
      if (el instanceof HTMLElement && document.contains(el)) el.focus();
    };
  }, []);

  const onKey = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    },
    [onClose],
  );

  if (typeof document === 'undefined') return null;

  // Derive the display name + a synthetic ref shape — ImageViewer only
  // reads `.name` and `.path/.id` from the ref when in directUrl mode, so
  // a path-free synthetic ref is fine.
  const baseName =
    name ??
    (() => {
      try {
        const u = new URL(src);
        return u.pathname.split('/').pop() ?? 'image';
      } catch {
        return src.split('/').pop() ?? 'image';
      }
    })();

  return createPortal(
    <div className="mp-modal__backdrop mp-lightbox" role="presentation" onClick={onClose}>
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={`Image preview: ${baseName}`}
        tabIndex={-1}
        className="mp-lightbox__panel"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={onKey}
      >
        <ImageViewer
          platform={platform}
          refOrPath={{ id: src, name: baseName }}
          directUrl={src}
        />
        <button
          type="button"
          className="mp-lightbox__close"
          aria-label="Close image preview"
          title="Close (Esc)"
          onClick={onClose}
        >
          <X size={16} strokeWidth={2.25} aria-hidden />
        </button>
      </div>
    </div>,
    document.body,
  );
}
